import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Loader2, Eye } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAppStore } from '../store/useAppStore';
import toast from 'react-hot-toast';
import appConfig from '../../../../config.json';

type Step = 'details' | 'pricing' | 'location' | 'preview';

export function ListTool() {
  const navigate = useNavigate();
  const { user, openAuthModal } = useAppStore();
  const [step, setStep] = useState<Step>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: '',
    description: '',
    brand: '',
    model: '',
    condition: '' as string,
    categoryId: '',
    pricePerDay: '',
    depositAmount: '',
    city: '',
    state: '',
    zipCode: '',
    minRentalDays: '1',
    termsAndConditions: '',
  });

  const update = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const steps: Step[] = ['details', 'pricing', 'location', 'preview'];
  const stepIdx = steps.indexOf(step);

  const handleNext = () => {
    if (step === 'location' && !user) {
      openAuthModal('register', '/list-tool');
      return;
    }
    const next = steps[stepIdx + 1];
    if (next) setStep(next);
  };

  const handleSubmit = async () => {
    if (!user) {
      openAuthModal('register');
      return;
    }
    setIsSubmitting(true);
    try {
      const { data } = await apiClient.post('/tools', {
        ...form,
        pricePerDay: parseFloat(form.pricePerDay),
        depositAmount: form.depositAmount ? parseFloat(form.depositAmount) : undefined,
        minRentalDays: parseInt(form.minRentalDays),
        status: 'AVAILABLE',
      });
      toast.success('Your tool is listed! 🎉');
      navigate(`/tools/${data.data.id}`);
    } catch {
      toast.error('Failed to create listing. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                            transition-all ${i <= stepIdx
                              ? 'bg-garden-500 text-white shadow-tim'
                              : 'bg-silver-100 text-silver-400'}`}
              >
                {i < stepIdx ? '✓' : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 w-12 sm:w-20 rounded transition-all ${
                  i < stepIdx ? 'bg-garden-500' : 'bg-silver-200'}`} />
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-silver-400 capitalize">
          Step {stepIdx + 1} of {steps.length}: {step}
        </p>
      </div>

      {/* Step: Details */}
      {step === 'details' && (
        <div className="space-y-4 animate-slide-up">
          <h2 className="font-display font-bold text-xl text-silver-800">Tell us about your tool</h2>

          <div>
            <label className="block text-sm font-medium text-silver-700 mb-1.5">Tool name *</label>
            <input value={form.name} onChange={(e) => update('name', e.target.value)}
              placeholder="e.g. DeWalt 20V Cordless Drill" className="input" required />
          </div>

          <div>
            <label className="block text-sm font-medium text-silver-700 mb-1.5">Category</label>
            <select value={form.categoryId} onChange={(e) => update('categoryId', e.target.value)}
              className="input">
              <option value="">Select a category</option>
              {appConfig.toolCategories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-silver-700 mb-1.5">Brand</label>
              <input value={form.brand} onChange={(e) => update('brand', e.target.value)}
                placeholder="DeWalt" className="input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-silver-700 mb-1.5">Model</label>
              <input value={form.model} onChange={(e) => update('model', e.target.value)}
                placeholder="DCD791D2" className="input" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-silver-700 mb-1.5">Condition</label>
            <div className="grid grid-cols-2 gap-2">
              {['NEW', 'LIKE_NEW', 'GOOD', 'FAIR'].map((c) => (
                <button key={c} type="button"
                  onClick={() => update('condition', c)}
                  className={`py-2 px-3 rounded-xl text-sm font-medium border transition-all ${
                    form.condition === c
                      ? 'bg-garden-500 text-white border-garden-500'
                      : 'bg-white text-silver-700 border-silver-200 hover:border-garden-400'
                  }`}>
                  {c.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-silver-700 mb-1.5">Description *</label>
            <textarea value={form.description} onChange={(e) => update('description', e.target.value)}
              rows={3} placeholder="Describe your tool, what it can do, any accessories included…"
              className="input resize-none" required />
          </div>

          {/* Image upload placeholder */}
          <div className="border-2 border-dashed border-silver-200 rounded-2xl p-6 text-center
                          hover:border-garden-400 transition-colors cursor-pointer group">
            <Upload size={24} className="mx-auto text-silver-400 group-hover:text-garden-500 mb-2 transition-colors" />
            <p className="text-sm text-silver-500">Upload photos of your tool</p>
            <p className="text-xs text-silver-400 mt-1">Up to 8 photos · JPG, PNG</p>
          </div>
        </div>
      )}

      {/* Step: Pricing */}
      {step === 'pricing' && (
        <div className="space-y-4 animate-slide-up">
          <h2 className="font-display font-bold text-xl text-silver-800">Set your price</h2>

          <div>
            <label className="block text-sm font-medium text-silver-700 mb-1.5">Daily rate *</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-silver-400 font-semibold">$</span>
              <input type="number" min="1" step="0.50"
                value={form.pricePerDay} onChange={(e) => update('pricePerDay', e.target.value)}
                placeholder="15.00" className="input pl-8" required />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-silver-700 mb-1.5">
              Security deposit <span className="text-silver-400 font-normal">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-silver-400 font-semibold">$</span>
              <input type="number" min="0" step="5"
                value={form.depositAmount} onChange={(e) => update('depositAmount', e.target.value)}
                placeholder="50.00" className="input pl-8" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-silver-700 mb-1.5">Minimum rental days</label>
            <select value={form.minRentalDays} onChange={(e) => update('minRentalDays', e.target.value)}
              className="input">
              {[1, 2, 3, 7].map((d) => (
                <option key={d} value={d}>{d} {d === 1 ? 'day' : 'days'}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-silver-700 mb-1.5">
              Terms & conditions <span className="text-silver-400 font-normal">(optional)</span>
            </label>
            <textarea value={form.termsAndConditions}
              onChange={(e) => update('termsAndConditions', e.target.value)}
              rows={3} placeholder="Any special instructions, damage policy, etc."
              className="input resize-none text-sm" />
          </div>
        </div>
      )}

      {/* Step: Location */}
      {step === 'location' && (
        <div className="space-y-4 animate-slide-up">
          <h2 className="font-display font-bold text-xl text-silver-800">Where's the tool located?</h2>
          <p className="text-sm text-silver-500">Only your city/neighborhood is shown publicly.</p>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-silver-700 mb-1.5">City *</label>
              <input value={form.city} onChange={(e) => update('city', e.target.value)}
                placeholder="Austin" className="input" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-silver-700 mb-1.5">State</label>
              <input value={form.state} onChange={(e) => update('state', e.target.value)}
                placeholder="TX" className="input" maxLength={2} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-silver-700 mb-1.5">ZIP code</label>
            <input value={form.zipCode} onChange={(e) => update('zipCode', e.target.value)}
              placeholder="78701" className="input" />
          </div>

          {!user && (
            <div className="bg-garden-50 rounded-2xl p-4 border border-garden-200">
              <p className="text-sm text-garden-700 font-medium">
                🦫 Almost there! You'll need a free account to publish your listing.
              </p>
              <p className="text-xs text-garden-600 mt-1">
                We've saved your progress — sign up and we'll post it right away.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && (
        <div className="space-y-4 animate-slide-up">
          <h2 className="font-display font-bold text-xl text-silver-800">Preview your listing</h2>
          <div className="card p-5 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-silver-800 text-lg leading-snug">{form.name || 'Tool name'}</h3>
              <span className="badge bg-garden-500 text-white text-base font-bold px-3 py-1 flex-shrink-0">
                ${form.pricePerDay || '0'}/day
              </span>
            </div>
            {form.description && (
              <p className="text-silver-500 text-sm leading-relaxed">{form.description}</p>
            )}
            <div className="flex flex-wrap gap-2 text-xs">
              {form.condition && (
                <span className="badge bg-silver-100 text-silver-700">{form.condition.replace('_', ' ')}</span>
              )}
              {form.city && (
                <span className="badge bg-beaver-cream text-beaver-brown">📍 {form.city}{form.state ? `, ${form.state}` : ''}</span>
              )}
              {form.depositAmount && (
                <span className="badge bg-blue-50 text-blue-700">💳 ${form.depositAmount} deposit</span>
              )}
            </div>
          </div>
          <p className="text-xs text-silver-400 text-center">
            <Eye size={12} className="inline mr-1" />
            This is how your listing will appear to renters
          </p>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex gap-3 mt-6">
        {stepIdx > 0 && (
          <button type="button" onClick={() => setStep(steps[stepIdx - 1])}
            className="btn-secondary flex-1">
            ← Back
          </button>
        )}
        {step !== 'preview' ? (
          <button
            type="button"
            onClick={handleNext}
            disabled={step === 'details' && (!form.name || !form.description)}
            className="btn-primary flex-1 disabled:opacity-40"
          >
            Continue →
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="btn-primary flex-1"
          >
            {isSubmitting ? (
              <Loader2 size={18} className="animate-spin mx-auto" />
            ) : (
              '🎉 Publish Listing'
            )}
          </button>
        )}
      </div>
    </div>
  );
}
